/**
 * The interventions calendar's model (spec 2026-10-25-22): twelve months back,
 * twelve ahead, one row per equipment (or asset), one marker per intervention.
 * Pure, so the classification, the window and the gap rows are unit-tested
 * without a DOM.
 *
 * Three things the old preventive strip mixed are told apart by the marker's
 * shape (its category), and its colour says the state:
 * - `maintenance`: an occurrence a maintenance contract generated
 *   (`properties.schedule.producer = contract`), or any other preventive visit;
 * - `check`: a regulatory periodic check (`producer = compliance`), or a
 *   periodic inspection rule's next due date when no occurrence carries it;
 * - `other`: everything else — a repair, works.
 */
import type { Intervention } from '../../hooks/useInterventions'
import type { AssetInspection } from '../../hooks/useComplianceApi'

export type CalendarCategory = 'maintenance' | 'check' | 'other'
export type CalendarState = 'planned' | 'ordered' | 'done' | 'overdue'
export type CalendarFilter = 'all' | CalendarCategory

export interface CalendarMonth {
    year: number
    month: number
    /** 0 is the current month, negative the past. */
    offset: number
}

export interface CalendarMarker {
    /** The intervention uid; `inspection:<evaluation uid>` for a due date read off a rule. */
    uid: string
    interventionUid?: string
    title: string
    date: Date
    category: CalendarCategory
    state: CalendarState
    /** The rule (or contract) a generated occurrence comes from: `properties.schedule.source_uid`. */
    sourceUid?: string
}

export interface CalendarRow {
    assetUid: string
    assetName: string
    markers: CalendarMarker[]
    /** Periodic checks the asset is subject to with no date at all in the window. */
    undatedChecks: string[]
}

export const MONTHS_BACK = 12
export const MONTHS_AHEAD = 12

export function calendarMonths(now: Date, back = MONTHS_BACK, ahead = MONTHS_AHEAD): CalendarMonth[] {
    const out: CalendarMonth[] = []
    for (let i = -back; i < ahead; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1)
        out.push({ year: d.getFullYear(), month: d.getMonth(), offset: i })
    }
    return out
}

export function interventionCategory(iv: Pick<Intervention, 'kind' | 'properties'>): CalendarCategory {
    const schedule = (iv.properties as Record<string, unknown> | undefined)?.schedule as { producer?: string } | undefined
    if (schedule?.producer === 'compliance') return 'check'
    if (schedule?.producer === 'contract') return 'maintenance'
    return iv.kind === 'preventive' ? 'maintenance' : 'other'
}

export function interventionState(iv: Pick<Intervention, 'status' | 'performed_at' | 'scheduled_at'>, now: Date): CalendarState {
    if (iv.performed_at || iv.status === 'done') return 'done'
    const due = iv.scheduled_at ? new Date(iv.scheduled_at) : null
    if (due && due < now) return 'overdue'
    return iv.status === 'planned' ? 'planned' : 'ordered'
}

function inWindow(date: Date, months: CalendarMonth[]): boolean {
    if (months.length === 0) return false
    const first = new Date(months[0].year, months[0].month, 1)
    const last = months[months.length - 1]
    const end = new Date(last.year, last.month + 1, 1)
    return date >= first && date < end
}

function pickLabel(label: Record<string, string> | undefined, lang: string): string {
    if (!label) return ''
    return label[lang] || label.en || Object.values(label)[0] || ''
}

export function buildCalendar(
    interventions: Intervention[],
    inspections: AssetInspection[],
    now: Date,
    lang: string,
    filter: CalendarFilter = 'all',
): { months: CalendarMonth[]; rows: CalendarRow[] } {
    const months = calendarMonths(now)
    const rows = new Map<string, CalendarRow>()
    const rowFor = (uid: string, name: string) => {
        let row = rows.get(uid)
        if (!row) {
            row = { assetUid: uid, assetName: name, markers: [], undatedChecks: [] }
            rows.set(uid, row)
        } else if (!row.assetName && name) {
            row.assetName = name
        }
        return row
    }

    for (const iv of interventions) {
        if (iv.status === 'cancelled') continue
        const state = interventionState(iv, now)
        const raw = state === 'done' ? (iv.performed_at ?? iv.scheduled_at) : iv.scheduled_at
        if (!raw) continue
        const date = new Date(raw)
        if (!inWindow(date, months)) continue
        const category = interventionCategory(iv)
        if (filter !== 'all' && filter !== category) continue
        const schedule = (iv.properties as Record<string, unknown> | undefined)?.schedule as { source_uid?: string } | undefined
        rowFor(iv.asset_uid, iv.asset?.name ?? '').markers.push({
            uid: iv.uid, interventionUid: iv.uid, title: iv.title, date, category, state, sourceUid: schedule?.source_uid,
        })
    }

    if (filter === 'all' || filter === 'check') {
        for (const insp of inspections) {
            // One rule at a time: an equipment under several periodic rules
            // keeps a marker (or a gap) for each rule no occurrence carries.
            // A done occurrence covers the past only: the rule's next due date
            // still shows unless an open occurrence already carries it.
            const carried = rows.get(insp.asset_uid)?.markers.filter((m) => m.category === 'check' && m.sourceUid === insp.rule_uid) ?? []
            const label = pickLabel(insp.label, lang)
            const due = insp.next_due_date ? new Date(insp.next_due_date) : null
            const dueShown = due && inWindow(due, months)
            if (carried.some((m) => m.state !== 'done') || (carried.length > 0 && !dueShown)) continue
            const row = rowFor(insp.asset_uid, insp.asset_name ?? '')
            if (dueShown) {
                row.markers.push({
                    uid: `inspection:${insp.evaluation_uid}`, title: label, date: due, category: 'check',
                    state: due < now ? 'overdue' : 'planned',
                })
            } else {
                row.undatedChecks.push(label)
            }
        }
    }

    const out = [...rows.values()].filter((r) => r.markers.length > 0 || r.undatedChecks.length > 0)
    for (const r of out) r.markers.sort((a, b) => a.date.getTime() - b.date.getTime())
    // Rows needing attention first (a gap or something overdue), then by name.
    const urgency = (r: CalendarRow) => (r.undatedChecks.length > 0 || r.markers.some((m) => m.state === 'overdue') ? 0 : 1)
    out.sort((a, b) => urgency(a) - urgency(b) || a.assetName.localeCompare(b.assetName))
    return { months, rows: out }
}
